<template>
  <div v-if="isLoading" class="mobile-loading">
    <b-spinner class="mobile-loading-spinner" />
  </div>

  <div v-else class="wynelover-mobile-page">
    <!-- Top navigation and language selector are provided by Index.vue. -->

    <!-- Hero: same visual and wording as Index_Desktop.vue -->
    <header class="mobile-hero">
      <div class="mobile-hero-copy">
        <h1 class="mobile-brand-title">
          <span>WYNE</span>LOVER
        </h1>
        <p class="mobile-brand-subtitle">
          {{ translate('the open digital wine ecosystem') }}
        </p>
      </div>
    </header>

    <!-- All four desktop tiles, arranged vertically -->
    <main class="mobile-products">
      <div class="mobile-product-grid">
        <article
          class="mobile-product-card"
          role="link"
          tabindex="0"
          :aria-label="translate('Explore WyneCellar')"
          @click="onWyneCellar"
          @keydown.enter="onWyneCellar"
          @keydown.space.prevent="onWyneCellar"
        >
          <img
            :src="require('@/assets/img/wynecellar.png')"
            class="mobile-product-image"
            alt="WyneCellar"
          >
          <div class="mobile-product-copy">
            <h2><span class="mobile-wyne-pink">WYNE</span>CELLAR</h2>
            <h3>{{ translate('your wines. your cellar.') }}</h3>
            <p>
              {{ translate('Manage your personal wine collection,') }}
              {{ translate('purchases, transactions and cellar locations.') }}
            </p>
            <div class="mobile-explore">
              {{ translate('Explore WyneCellar') }} <span aria-hidden="true">→</span>
            </div>
          </div>
        </article>

        <article
          class="mobile-product-card"
          role="link"
          tabindex="0"
          :aria-label="translate('Explore WyneTaster')"
          @click="onWyneTaster"
          @keydown.enter="onWyneTaster"
          @keydown.space.prevent="onWyneTaster"
        >
          <img
            :src="require('@/assets/img/wynetaster.png')"
            class="mobile-product-image"
            alt="WyneTaster"
            loading="lazy"
          >
          <div class="mobile-product-copy">
            <h2><span class="mobile-wyne-green">WYNE</span>TASTER</h2>
            <h3>{{ translate('taste. enjoy. remember.') }}</h3>
            <p>
              {{ translate('Create and manage tasting notes, organise') }}
              {{ translate('tastings and explore wines with structured templates.') }}
            </p>
            <div class="mobile-explore">
              {{ translate('Explore WyneTaster') }} <span aria-hidden="true">→</span>
            </div>
          </div>
        </article>

        <article
          class="mobile-product-card"
          role="link"
          tabindex="0"
          :aria-label="translate('Explore WyneMaker')"
          @click="onWyneMaker"
          @keydown.enter="onWyneMaker"
          @keydown.space.prevent="onWyneMaker"
        >
          <img
            :src="require('@/assets/img/wynemaker.jpg')"
            class="mobile-product-image"
            alt="WyneMaker"
            loading="lazy"
          >
          <div class="mobile-product-copy">
            <h2><span class="mobile-wyne-pink">WYNE</span>MAKER</h2>
            <h3>{{ translate('for wine producers.') }}</h3>
            <p>
              {{ translate('Create your winery page, share your wines') }}
              {{ translate('and connect with wine lovers.') }}
            </p>
            <div class="mobile-explore">
              {{ translate('Explore WyneMaker') }} <span aria-hidden="true">→</span>
            </div>
          </div>
        </article>

        <article
          class="mobile-product-card"
          role="link"
          tabindex="0"
          :aria-label="translate('Explore WyneAtlas')"
          @click="onWyneAtlas"
          @keydown.enter="onWyneAtlas"
          @keydown.space.prevent="onWyneAtlas"
        >
          <img
            :src="require('@/assets/img/wyneatlas.png')"
            class="mobile-product-image"
            alt="WyneAtlas"
            loading="lazy"
          >
          <div class="mobile-product-copy">
            <h2><span class="mobile-wyne-green">WYNE</span>ATLAS</h2>
            <h3>{{ translate('explore wine regions.') }}</h3>
            <p>
              {{ translate('Discover countries, regions and their signature wines.') }}
              {{ translate('A growing guide to the world of wine.') }}
            </p>
            <div class="mobile-explore">
              {{ translate('Explore WyneAtlas') }} <span aria-hidden="true">→</span>
            </div>
          </div>
        </article>
      </div>
    </main>

    <!-- Adapted desktop footer -->
    <footer class="mobile-footer">
      <div class="mobile-footer-content">
        <p class="mobile-footer-wordmark">
          <span class="mobile-footer-wyne">WYNE</span><span>LOVER</span>
        </p>
        <div class="mobile-footer-url">www.wynelover.com</div>
        <div class="mobile-footer-tagline">
          {{ translate('the open digital wine ecosystem') }}
        </div>
        <div class="mobile-footer-nav">
          {{ translate('explore') }}
          <span>·</span>
          {{ translate('learn') }}
          <span>·</span>
          {{ translate('taste') }}
          <span>·</span>
          {{ translate('share') }}
        </div>
      </div>
      <div class="mobile-copyright">
        {{ translate("funware for wine lovers developed and ©copyrighted by N'JOY Information Technology") }}
      </div>
    </footer>
  </div>
</template>

<script>
import Cookies from 'js-cookie'
import { translate_word } from '../assets/js/translate.js'

const supportedLanguages = ['en', 'fr', 'de', 'es', 'it', 'nl', 'pt']

export default {
  name: 'IndexMobile',

  data() {
    return {
      isLoading: true
    }
  },

  computed: {
    currentLanguage() {
      return this.$store.state.language
    }
  },

  mounted() {
    // The parent Index.vue also initializes the language. Keep the mobile
    // component safe when it is mounted directly or after a layout change.
    let language = Cookies.get('wynelover.language')
    if (!supportedLanguages.includes(language)) language = 'en'
    this.$store.commit('setLanguage', language)
    this.isLoading = false
  },

  methods: {
    translate(string) {
      return translate_word(string, this.currentLanguage)
    },

    onWyneCellar() {
      window.open('https://www.wynecellar.com', '_blank', 'noopener,noreferrer')
    },

    onWyneTaster() {
      window.open('https://www.wynetaster.com', '_blank', 'noopener,noreferrer')
    },

    onWyneMaker() {
      window.open('https://www.wynemaker.com', '_blank', 'noopener,noreferrer')
    },

    onWyneAtlas() {
      window.location.href = '/atlas/'
    }
  }
}
</script>

<style scoped>
.wynelover-mobile-page {
  width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
  background: #ffffff;
  color: #555d68;
  font-family: Arial, Helvetica, sans-serif;
}

.wynelover-mobile-page * { box-sizing: border-box; }

/* HERO */
.mobile-hero {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: clamp(240px, 70vw, 345px);
  padding: 24px 12px 34px;
  overflow: hidden;
  background: url('../assets/img/wynelover.jpg') center center / cover no-repeat;
  text-align: center;
}

.mobile-hero::before {
  content: '';
  position: absolute;
  inset: 0 0 auto;
  height: 85px;
  background: linear-gradient(to bottom,
    rgba(255,255,255,1) 0%,
    rgba(255,255,255,.75) 25%,
    rgba(255,255,255,0) 100%);
  pointer-events: none;
}

.mobile-hero-copy {
  position: relative;
  z-index: 1;
  width: 100%;
}

.mobile-brand-title {
  margin: 0;
  color: #ffffff !important;
  font-size: clamp(36px, 10vw, 73px) !important;
  font-weight: 400;
  letter-spacing: -0.025em;
  line-height: 1.05;
  white-space: nowrap;
  text-shadow: 0 2px 5px rgba(0,0,0,.45);
}

.mobile-brand-title span { color: #6d7a52; font-weight: 700; }

.mobile-brand-subtitle {
  max-width: 92%;
  margin: 7px auto 0;
  color: #ffffff;
  font-size: clamp(13px, 3.8vw, 20px) !important;
  font-weight: 700;
  line-height: 1.3;
  text-shadow: 0 1px 5px rgba(0,0,0,.68);
}

/* SINGLE-COLUMN TILES */
.mobile-products {
  position: relative;
  z-index: 2;
  width: 100%;
  padding: 16px clamp(12px, 3.5vw, 26px) 32px;
  margin: -24px 0 0;
  background: #ffffff;
}

.mobile-product-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: clamp(16px, 3.3vw, 24px);
  max-width: 590px;
  margin: 0 auto;
}

.mobile-product-card {
  min-width: 0;
  overflow: hidden;
  border: 1px solid #ececec;
  border-radius: 7px;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(0,0,0,.065);
  text-align: left;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.mobile-product-card:focus-visible {
  outline: 3px solid #6d7a52;
  outline-offset: 3px;
}

.mobile-product-image {
  display: block;
  width: 100%;
  height: clamp(158px, 47vw, 252px);
  object-fit: cover;
}

.mobile-product-copy {
  padding: clamp(17px, 4.6vw, 26px);
}

.mobile-product-copy h2 {
  margin: 0 0 7px;
  color: #1a1a1a;
  font-size: clamp(22px, 6vw, 29px) !important;
  font-weight: 700;
  letter-spacing: .025em;
  line-height: 1.12;
  overflow-wrap: anywhere;
}

.mobile-product-copy h3 {
  margin: 0 0 12px;
  color: #707070;
  font-size: clamp(15px, 4.1vw, 18px) !important;
  font-style: italic;
  font-weight: 600;
  line-height: 1.35;
}

.mobile-product-copy p {
  margin: 0 0 16px;
  color: #5a6270;
  font-size: clamp(13px, 3.75vw, 15px) !important;
  line-height: 1.5;
}

.mobile-wyne-green { color: #6d7a52; }
.mobile-wyne-pink { color: #c39382; }

.mobile-explore {
  color: #707070;
  font-size: clamp(13px, 3.7vw, 15px);
  font-weight: 700;
  line-height: 1.4;
}

.mobile-explore span {
  display: inline-block;
  margin-left: 7px;
  font-size: 18px;
  font-weight: 400;
}

/* MOBILE FOOTER, USING THE DESKTOP PHOTOGRAPH/GRADIENT */
.mobile-footer {
  position: relative;
  width: 100%;
  height: clamp(252px, 70vw, 310px);
  overflow: hidden;
  color: #ffffff;
  background:
    linear-gradient(to bottom,
      #ffffff 0%,
      rgba(255,255,255,.96) 8%,
      rgba(255,255,255,.52) 22%,
      rgba(255,255,255,0) 40%),
    linear-gradient(to bottom,
      rgba(0,0,0,0) 44%,
      rgba(0,0,0,.68) 100%),
    url('../assets/img/wynelover.jpg') center 48% / cover no-repeat;
  text-align: center;
}

.mobile-footer-content {
  position: absolute;
  right: 12px;
  bottom: 68px;
  left: 12px;
  text-shadow: 0 1px 4px rgba(0,0,0,.6);
}

.mobile-footer-wordmark {
  margin: 0;
  font-size: clamp(29px, 8.5vw, 39px) !important;
  letter-spacing: .3px;
  line-height: 1;
}

.mobile-footer-wyne { color: #6d7a52; font-weight: 700; }
.mobile-footer-url { margin-top: 5px; font-size: 13px; font-weight: 600; }
.mobile-footer-tagline {
  margin-top: 4px;
  font-size: clamp(12px, 3.5vw, 14px);
  line-height: 1.3;
}
.mobile-footer-nav {
  margin-top: 9px;
  font-size: clamp(10px, 3vw, 12px);
  font-weight: 700;
  letter-spacing: clamp(1px, .35vw, 2px);
  line-height: 1.5;
}
.mobile-footer-nav span { margin: 0 5px; }
.mobile-copyright {
  position: absolute;
  right: 12px;
  bottom: 13px;
  left: 12px;
  font-size: clamp(10px, 2.7vw, 12px);
  line-height: 1.35;
  text-shadow: 0 1px 4px rgba(0,0,0,.7);
}

/* LOADING */
.mobile-loading {
  display: flex;
  min-height: 75vh;
  align-items: center;
  justify-content: center;
  background: #ffffff;
}
.mobile-loading-spinner { color: #6d7a52 !important; }

@media (max-width: 360px) {
  .mobile-products { padding-right: 12px; padding-left: 12px; }
  .mobile-brand-title { letter-spacing: -0.05em; }
  .mobile-product-copy { padding: 16px; }
  .mobile-footer-content { bottom: 72px; }
}

@media (prefers-reduced-motion: no-preference) {
  .mobile-product-card { transition: box-shadow .2s ease, transform .2s ease; }
  .mobile-product-card:active { transform: scale(.994); }
}
</style>
